import type { FC, ReactNode } from 'react'
import type { ContactIntent } from '@/constants/contact'

import { Reveal } from '@/components/generals/reveal'
import { PillButton } from '@/components/generals/pill-button'
import { useContact } from '@/components/generals/contact-dialog'

interface CtaAction {
    label: string
    variant: 'primary' | 'secondary'
    href?: string
    intent?: ContactIntent
}

interface CtaSectionProps {
    id?: string
    title: string
    accent: string
    actions: Array<CtaAction>
}

export const CtaSection: FC<CtaSectionProps> = ({ id = 'contact', title, accent, actions }): ReactNode => {
    const { openContact } = useContact()

    return (
        <section id={id} aria-labelledby={`${id}-title`} className='py-24 md:py-32'>
            <div className='fc-container'>
                <Reveal className='relative isolate overflow-hidden rounded-[32px] bg-[#fbfaff] px-6 py-20 text-center ring-1 ring-inset ring-[#ecebf3] sm:px-10 md:py-28'>
                    <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_right,rgba(93,45,230,0.07)_1px,transparent_1px),linear-gradient(to_bottom,rgba(93,45,230,0.07)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,#000_15%,transparent_70%)]' />
                    <div aria-hidden='true' className='pointer-events-none absolute left-1/2 top-0 -z-10 h-[360px] w-[760px] max-w-[140%] -translate-x-1/2 -translate-y-1/3 rounded-full bg-[radial-gradient(closest-side,rgba(114,65,255,0.22),rgba(77,124,255,0.12)_55%,transparent)] blur-2xl' />

                    <h2 id={`${id}-title`} className='mx-auto max-w-[820px] text-[clamp(2.25rem,4.8vw,4.25rem)] font-medium leading-[1.04] tracking-[-0.04em]'>
                        <span className='block text-ink'>{title}</span>
                        <span className='fc-text-gradient block pb-1'>{accent}</span>
                    </h2>

                    <div className='mt-10 flex flex-wrap items-center justify-center gap-3'>
                        {actions.map(action => (
                            <PillButton
                                key={action.label}
                                variant={action.variant}
                                chevron={action.variant === 'primary'}
                                href={action.href}
                                onClick={action.intent ? () => openContact({ intent: action.intent }) : undefined}
                                size='lg'
                            >
                                {action.label}
                            </PillButton>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
